import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import DefaultApiService from "../../../../../infrastructure/api/default";
import { getManagementDashboardFunnel } from "../../../../../infrastructure/api/managementDashboard";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import GrowthConversionAnalyticsPanel from "../../components/GrowthConversionAnalyticsPanel";
import RequestStageTimeline from "../../components/RequestStageTimeline";
import {
  DEFAULT_GROWTH_AGGREGATION,
  DEFAULT_GROWTH_PERIOD_DAYS,
  GROWTH_ANALYTICS_AGGREGATIONS,
  GROWTH_ANALYTICS_PERIODS,
  MONTHLY_COMPARE_AGGREGATION,
  normalizeGrowthAnalytics,
} from "../../components/growthAnalyticsConstants";
import {
  getApiErrorMessage,
  isApiSuccess,
  pick,
  toInputDate,
} from "../dashboard/managementDashboardUtils";

const buildPeriodFilter = (periodDays) => {
  const today = new Date();
  const from = new Date(today);
  from.setDate(from.getDate() - (Number(periodDays) || DEFAULT_GROWTH_PERIOD_DAYS) + 1);
  return {
    dateFrom: toInputDate(from),
    dateTo: toInputDate(today),
  };
};

const GrowthAnalyticsPage = () => {
  const [loading, setLoading] = useState(true);
  const [growthAnalytics, setGrowthAnalytics] = useState(null);
  const [periodDays, setPeriodDays] = useState(DEFAULT_GROWTH_PERIOD_DAYS);
  const [aggregation, setAggregation] = useState(DEFAULT_GROWTH_AGGREGATION);

  const [funnelLoading, setFunnelLoading] = useState(true);
  const [funnelError, setFunnelError] = useState(null);
  const [funnelStages, setFunnelStages] = useState([]);
  const [cohortSize, setCohortSize] = useState(0);

  const loadAnalytics = useCallback(async (days, agg) => {
    try {
      setLoading(true);
      const res = await DefaultApiService.getApi(
        "SLN/Management/GrowthAnalytics/GetTrend",
        { periodDays: days, aggregation: agg },
        true
      );
      if (res?.isSuccess && res.data) {
        setGrowthAnalytics(normalizeGrowthAnalytics(res.data));
      }
    } catch {
      setGrowthAnalytics(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadFunnel = useCallback(async (days) => {
    try {
      setFunnelLoading(true);
      setFunnelError(null);
      const res = await getManagementDashboardFunnel(buildPeriodFilter(days));
      if (isApiSuccess(res) && res.data) {
        setFunnelStages(pick(res.data, "stages", "Stages") || []);
        setCohortSize(Number(pick(res.data, "cohortSize", "CohortSize") ?? 0));
      } else {
        setFunnelStages([]);
        setCohortSize(0);
        setFunnelError(
          getApiErrorMessage(res, "دریافت تایم‌لاین مراحل پرونده‌ها ناموفق بود.")
        );
      }
    } catch {
      setFunnelStages([]);
      setCohortSize(0);
      setFunnelError("خطا در دریافت تایم‌لاین مراحل پرونده‌ها.");
    } finally {
      setFunnelLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAnalytics(periodDays, aggregation);
  }, [loadAnalytics, periodDays, aggregation]);

  useEffect(() => {
    loadFunnel(periodDays);
  }, [loadFunnel, periodDays]);

  const handlePeriodChange = (_, next) => {
    if (next == null) return;
    const option = GROWTH_ANALYTICS_PERIODS.find((p) => p.days === next);
    if (!option?.enabled) return;
    setPeriodDays(next);
  };

  const handleAggregationChange = (_, next) => {
    if (next == null) return;
    setAggregation(next);
    if (next === MONTHLY_COMPARE_AGGREGATION && periodDays < 90) {
      setPeriodDays(365);
    }
  };

  return (
    <Box sx={{ px: { xs: 1, md: 2 }, py: { xs: 1, md: 1.5 }, background: "#F6F8FB" }}>
      <BreadcrumbsComponent />
      <Typography variant="h5" fontWeight={700} sx={{ mt: 1, mb: 1 }}>
        تحلیل رشد و تبدیل
      </Typography>

      <Box sx={{ mb: 2, display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={periodDays}
          onChange={handlePeriodChange}
          sx={{ flexWrap: "wrap" }}
        >
          {GROWTH_ANALYTICS_PERIODS.map((option) => (
            <ToggleButton
              key={option.days}
              value={option.days}
              disabled={!option.enabled}
              sx={{ fontSize: 12, px: 1.5 }}
            >
              {option.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <ToggleButtonGroup
          exclusive
          size="small"
          color="primary"
          value={aggregation}
          onChange={handleAggregationChange}
          sx={{ flexWrap: "wrap" }}
        >
          {GROWTH_ANALYTICS_AGGREGATIONS.map((option) => (
            <ToggleButton
              key={option.value}
              value={option.value}
              sx={{ fontSize: 12, px: 1.5, fontWeight: 700 }}
            >
              {option.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Box
        sx={{
          borderRadius: "16px",
          bgcolor: "#fff",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          p: 2,
          mb: 2,
        }}
      >
        <RequestStageTimeline
          stages={funnelStages}
          loading={funnelLoading}
          error={funnelError}
          cohortSize={cohortSize}
          onRetry={() => loadFunnel(periodDays)}
        />
      </Box>

      <Box
        sx={{
          borderRadius: "16px",
          bgcolor: "#fff",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          p: 2,
        }}
      >
        <GrowthConversionAnalyticsPanel
          analytics={growthAnalytics}
          loading={loading}
          height={360}
          periodDays={periodDays}
          aggregation={aggregation}
          showPeriodFilter={false}
          showAggregationFilter={false}
          showFullAnalyticsLink={false}
        />
      </Box>
    </Box>
  );
};

export default GrowthAnalyticsPage;
